<script lang="ts">
	// UC-18, a manager on the till: correct a known customer's name, and
	// edit or take away one of their saved addresses. It stacks on the
	// delivery details, and Back returns there with nothing lost.
	//
	// Every change goes to the customer, not to this order, and past orders
	// keep the address they were placed with. An address is edited in place,
	// one at a time, with this shop's areas; while one is open the others
	// wait. Removing takes the address away at once with a ten-second Undo,
	// shown by the till, and the server hears of it when the Undo runs out;
	// only the owner deletes a customer, from the dashboard.

	import AddressEdit from './AddressEdit.svelte';
	import Band from '../components/Band.svelte';
	import Button from '../components/Button.svelte';
	import Field from '../components/Field.svelte';
	import Sheet from '../components/Sheet.svelte';
	import { message, type Language } from '../i18n/messages';
	import {
		editCustomerAddress,
		removeAddressWithUndo,
		renameCustomer,
		type AddressEdit as Edit,
		type Customer,
		type EditOutcome,
	} from '../till/customers.svelte';
	import type { components } from '../api/types';

	type Area = components['schemas']['AreaBody'];

	interface Props {
		open: boolean;
		language: Language;
		customer: Customer | null;
		areas: Area[];
		minorDigits: number;
		onBack: () => void;
		/** The name was corrected; the delivery details may follow it. */
		onRenamed: (before: string, after: string) => void;
		/** Deleted from the dashboard meanwhile: the till should ask again. */
		onGone: () => void;
	}

	let { open, language, customer, areas, minorDigits, onBack, onRenamed, onGone }: Props = $props();

	let name = $state('');
	let saving = $state(false);
	let editing = $state('');
	let failure = $state('');

	// Each opening starts from the saved name, with no address open.
	$effect(() => {
		if (open) {
			name = customer?.name ?? '';
			failure = '';
			editing = '';
		}
	});

	const trimmed = $derived(name.trim());
	const addresses = $derived(customer?.addresses ?? []);

	function report(result: EditOutcome): boolean {
		if (result === 'saved') return true;
		if (result === 'gone') {
			onGone();
			return false;
		}
		failure = message(language, 'ordersFailed');
		return false;
	}

	async function save() {
		if (!customer || !trimmed || trimmed === customer.name || saving) return;
		saving = true;
		failure = '';
		const before = customer.name;
		const result = await renameCustomer(customer, trimmed);
		saving = false;
		if (report(result)) {
			onRenamed(before, trimmed);
			onBack();
		}
	}

	async function edit(addressId: string, change: Edit) {
		if (!customer) return 'failed';
		failure = '';
		const result = await editCustomerAddress(customer, addressId, change);
		if (result === 'taken') return result;
		if (report(result)) editing = '';
		return result;
	}

	function remove(addressId: string) {
		if (!customer) return;
		failure = '';
		removeAddressWithUndo(customer, addressId);
	}
</script>

<Sheet
	{open}
	level={2}
	title={message(language, 'customerTitle')}
	backLabel={message(language, 'deliveryTitle')}
	{onBack}
	backTestId="customer-back"
	primaryLabel={message(language, 'customerSaveName')}
	primaryTestId="customer-save"
	primaryDisabled={!customer || !trimmed || trimmed === customer.name}
	primaryState={saving ? 'pending' : 'idle'}
	pendingLabel={message(language, 'saving')}
	onPrimary={() => void save()}
	testId="customer-sheet"
>
	{#if customer}
		<div class="body" data-scroll="">
			{#if failure}
				<Band tone="flag" testId="customer-error">{failure}</Band>
			{/if}
			<div class="fields">
				<Field
					testId="customer-phone"
					label={message(language, 'deliveryPhone')}
					value={customer.phone}
					mono
				/>
				<Field
					testId="customer-name"
					label={message(language, 'deliveryName')}
					value={name}
					maxLength={120}
					error={trimmed ? undefined : message(language, 'customerNameRequired')}
					onInput={(value) => (name = value)}
				/>
			</div>
			<div class="saved">
				<span class="heading">{message(language, 'customerAddresses')}</span>
				{#if addresses.length === 0}
					<p class="none" data-testid="customer-no-addresses">
						{message(language, 'customerNoAddresses')}
					</p>
				{:else}
					<div class="list">
						{#each addresses as address (address.id)}
							{@const areaName = areas.find((area) => area.id === address.areaId)?.name}
							{@const meta = [address.landmark, areaName].filter(Boolean).join(' · ')}
							{@const off = editing !== '' && editing !== address.id}
							<div class="address" data-testid="customer-address-{address.id}">
								{#if editing === address.id}
									<AddressEdit
										{language}
										{minorDigits}
										testId="customer-address-edit"
										address={address.address}
										landmark={address.landmark ?? ''}
										areaId={address.areaId ?? ''}
										{areas}
										areasTitle={message(language, 'deliveryArea')}
										size="md"
										onCancel={() => (editing = '')}
										onSave={(change) => edit(address.id, change)}
									/>
								{:else}
									<div class="view">
										<span class="text">
											<span class="line">{address.address}</span>
											{#if meta}
												<span class="area">{meta}</span>
											{/if}
										</span>
										<Button
											testId="customer-address-edit-{address.id}"
											label={message(language, 'customerEditAddress')}
											tone="text"
											size="md"
											disabled={off}
											onPress={() => (editing = address.id)}
										/>
										<Button
											testId="customer-address-remove-{address.id}"
											label={message(language, 'remove')}
											tone="text-quiet"
											size="md"
											disabled={off}
											onPress={() => remove(address.id)}
										/>
									</div>
								{/if}
							</div>
						{/each}
					</div>
				{/if}
			</div>
			<p class="note">{message(language, 'customerTillNote')}</p>
		</div>
	{/if}
</Sheet>

<style>
	.body {
		flex: 1;
		min-block-size: 0;
		overflow-y: auto;
		overflow-x: hidden;
		display: flex;
		flex-direction: column;
		gap: var(--space-7);
	}

	.fields {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
		gap: var(--space-7);
	}

	.saved {
		display: flex;
		flex-direction: column;
		gap: var(--space-4);
	}

	.heading {
		font-size: var(--text-base);
		font-weight: 600;
		color: var(--text-strong);
	}

	.none {
		margin: 0;
		font-size: var(--text-sm);
		color: var(--text-body);
	}

	.list {
		display: flex;
		flex-direction: column;
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-card);
		background: var(--surface-card);
	}

	.address {
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
		padding: var(--space-4) var(--space-5);
	}

	.view {
		display: flex;
		align-items: center;
		gap: var(--space-4);
	}

	.address + .address {
		border-block-start: var(--border-width) solid var(--border-inset);
	}

	.text {
		flex: 1;
		min-inline-size: 0;
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
	}

	.line {
		font-size: var(--text-sm);
		font-weight: 600;
		color: var(--text-strong);
		text-wrap: pretty;
	}

	.area {
		font-size: var(--text-2xs);
		color: var(--text-body);
		text-wrap: pretty;
	}

	.note {
		margin: 0;
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}
</style>
